import React, { useState, useMemo } from 'react';
import { useFinance } from '../context/FinanceContext';

export type TreeItem = {
  name: string;
  value: number;
  totalRaw: number;
  timeframeValue: number;
  roi: number;
  percentOfPortfolio: number;
  color: string;
};

export type TreeRect = { x: number; y: number; w: number; h: number; item: TreeItem };

/**
 * Squarified treemap layout with power-dampening & minimum weight floor
 * to spread rectangles gracefully so every category is visible and readable.
 */
export function squarify(data: TreeItem[], W: number, H: number): TreeRect[] {
  if (!data.length) return [];
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return [];

  const count = data.length;
  // Floor ensures even the smallest category gets sufficient canvas area to be visible
  const minWeightFrac = Math.min(0.07, 0.9 / Math.max(count, 1));

  // Power-dampened weights prevent a single 70%+ category from squeezing other categories into invisible slivers
  const rawDamped = data.map(d => ({
    ...d,
    calcWeight: Math.pow(Math.max(d.value, 1), 0.6)
  }));
  const sumDamped = rawDamped.reduce((s, d) => s + d.calcWeight, 0);

  const normalized = rawDamped.map(d => {
    const rawFrac = sumDamped > 0 ? d.calcWeight / sumDamped : 1 / count;
    const finalFrac = Math.max(rawFrac, minWeightFrac);
    return { ...d, layoutWeight: finalFrac };
  });
  const sumFinal = normalized.reduce((s, d) => s + d.layoutWeight, 0);

  const sorted = [...normalized]
    .sort((a, b) => b.value - a.value)
    .map(d => ({ ...d, value: (d.layoutWeight / sumFinal) * W * H }));

  const result: TreeRect[] = [];

  function layoutRow(items: typeof sorted, x: number, y: number, w: number, h: number) {
    const rowTotal = items.reduce((s, d) => s + d.value, 0);
    const isHoriz = w >= h;
    let offset = 0;
    items.forEach(item => {
      const frac = rowTotal > 0 ? item.value / rowTotal : 0;
      if (isHoriz) {
        const iW = frac * w;
        result.push({ x: x + offset, y, w: iW, h, item });
        offset += iW;
      } else {
        const iH = frac * h;
        result.push({ x, y: y + offset, w, h: iH, item });
        offset += iH;
      }
    });
  }

  function slice(items: typeof sorted, x: number, y: number, w: number, h: number) {
    if (items.length === 0) return;
    if (items.length === 1) { layoutRow(items, x, y, w, h); return; }
    const total2 = items.reduce((s, d) => s + d.value, 0);
    if (total2 <= 0) return;
    let best = Infinity;
    let splitIdx = 1;
    for (let i = 1; i < items.length; i++) {
      const aSum = items.slice(0, i).reduce((s, d) => s + d.value, 0);
      const bSum = total2 - aSum;
      const aFrac = aSum / total2;
      const bFrac = bSum / total2;
      const isHoriz = w >= h;
      const aW = isHoriz ? aFrac * w : w;
      const aH = isHoriz ? h : aFrac * h;
      const bW = isHoriz ? bFrac * w : w;
      const bH = isHoriz ? h : bFrac * h;

      const score = Math.max(
        ...items.slice(0, i).map(d => {
          const f = aSum > 0 ? d.value / aSum : 0;
          return isHoriz ? Math.max(aH / (f * aW + .001), (f * aW) / (aH + .001)) : Math.max(aW / (f * aH + .001), (f * aH) / (aW + .001));
        }),
        ...items.slice(i).map(d => {
          const f = bSum > 0 ? d.value / bSum : 0;
          return isHoriz ? Math.max(bH / (f * bW + .001), (f * bW) / (bH + .001)) : Math.max(bW / (f * bH + .001), (f * bH) / (bW + .001));
        })
      );
      if (score < best) {
        best = score;
        splitIdx = i;
      }
    }
    const aItems = items.slice(0, splitIdx);
    const bItems = items.slice(splitIdx);
    const aSum = aItems.reduce((s, d) => s + d.value, 0);
    const aFrac = aSum / total2;
    const isHoriz = w >= h;
    if (isHoriz) {
      const aW = aFrac * w;
      layoutRow(aItems, x, y, aW, h);
      slice(bItems, x + aW, y, w - aW, h);
    } else {
      const aH = aFrac * h;
      layoutRow(aItems, x, y, w, aH);
      slice(bItems, x, y + aH, w, h - aH);
    }
  }

  slice(sorted, 0, 0, W, H);
  return result;
}

const fmt = (n: number, d = 0) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

export interface PortfolioTreemapProps {
  data: TreeItem[];
  roi: number;
  positive: boolean;
  currency: string;
  timeframe: string;
  roiValue?: number;
  portfolioValue?: number;
  height?: number;
  fillBox?: boolean;
}

export const PortfolioTreemap: React.FC<PortfolioTreemapProps> = ({
  data,
  roi,
  positive,
  currency,
  timeframe,
  roiValue,
  portfolioValue,
  height,
  fillBox
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const { isDarkMode } = useFinance();

  const P = {
    s1: isDarkMode ? '#0D1625' : '#ffffff',
    s2: isDarkMode ? '#111E33' : '#ffffff',
    s3: isDarkMode ? '#162340' : '#f1f5f9',
    border: isDarkMode ? '#1A2940' : '#e2e8f0',
    muted: isDarkMode ? '#475569' : '#64748b',
    subtle: isDarkMode ? '#94a3b8' : '#94a3b8',
    text: isDarkMode ? '#F8FAFC' : '#0f172a',
    teal: isDarkMode ? '#2DD4BF' : '#0d9488',
    rose: isDarkMode ? '#F87171' : '#e11d48',
  };

  const calculatedPortfolioValue = useMemo(() => {
    if (portfolioValue !== undefined) return portfolioValue;
    return data.reduce((s, d) => s + (d.totalRaw || d.value), 0);
  }, [portfolioValue, data]);

  const topCategory = useMemo(() => {
    if (!data || data.length === 0) return null;
    return [...data].sort((a, b) => (b.percentOfPortfolio || b.value) - (a.percentOfPortfolio || a.value))[0];
  }, [data]);

  const W = 520;
  const H = height || (fillBox ? 380 : 330);
  const rects = useMemo(() => squarify(data, W, H), [data, W, H]);

  return (
    <div style={{ position: 'relative', height: fillBox ? '100%' : 'auto', display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between', gap: 12 }}>
      {/* SVG Treemap Canvas */}
      <div style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', background: P.s3, flex: 1, minHeight: fillBox ? 340 : 290, display: 'flex', alignItems: 'stretch', width: '100%' }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ display: 'block', width: '100%', height: '100%' }}
        >
          <defs>
            {data.map((_, i) => (
              <filter key={i} id={`ptm-glow-${i}`}>
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            ))}
          </defs>
          {rects.map((r, i) => {
            const pct = r.item.percentOfPortfolio.toFixed(1);
            const isHov = hovered === i;
            const pad = 3;

            const catName = r.item.name;
            const catTotalVal = r.item.totalRaw;
            const catTimeframeVal = r.item.timeframeValue;
            const catRoi = r.item.roi;

            const isLarge = r.w >= 100 && r.h >= 55;
            const isMedium = (r.w >= 55 && r.h >= 36) && !isLarge;
            const isSmall = !isLarge && !isMedium;

            return (
              <g
                key={i}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Base Tile */}
                <rect
                  x={r.x + pad}
                  y={r.y + pad}
                  width={Math.max(r.w - pad * 2, 0)}
                  height={Math.max(r.h - pad * 2, 0)}
                  rx={10}
                  fill={r.item.color}
                  opacity={isHov ? 1 : 0.88}
                  style={{ transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />

                {/* Accent Outline */}
                <rect
                  x={r.x + pad}
                  y={r.y + pad}
                  width={Math.max(r.w - pad * 2, 0)}
                  height={Math.max(r.h - pad * 2, 0)}
                  rx={10}
                  fill="none"
                  stroke={isHov ? '#ffffff' : 'rgba(255,255,255,0.22)'}
                  strokeWidth={isHov ? 2.5 : 1}
                  style={{ transition: 'all 0.2s ease-in-out' }}
                />

                {/* Tier 1: Large Tile */}
                {isLarge && (
                  <g style={{ pointerEvents: 'none' }}>
                    <text
                      x={r.x + 12}
                      y={r.y + 22}
                      fill="#ffffff"
                      fontSize={r.w > 130 ? 13 : 11.5}
                      fontWeight={700}
                      fontFamily="'Sora', sans-serif"
                    >
                      {r.w > 110 ? catName : (catName.length > 9 ? catName.slice(0, 8) + '..' : catName)}
                    </text>

                    <text
                      x={r.x + r.w - 12}
                      y={r.y + 22}
                      textAnchor="end"
                      fill="rgba(255,255,255,0.85)"
                      fontSize={11}
                      fontWeight={800}
                      fontFamily="'Sora', sans-serif"
                    >
                      {pct}%
                    </text>

                    {r.h > 80 && r.w > 85 && (
                      <>
                        <text
                          x={r.x + 12}
                          y={r.y + 40}
                          fill={catTimeframeVal >= 0 ? '#a7f3d0' : '#fca5a5'}
                          fontSize={9.5}
                          fontFamily="'Sora', sans-serif"
                          fontWeight={700}
                        >
                          {timeframe}: {catTimeframeVal >= 0 ? '+' : '-'}{currency}{fmt(Math.abs(catTimeframeVal))}
                        </text>
                        <text
                          x={r.x + 12}
                          y={r.y + 54}
                          fill={catRoi >= 0 ? '#34d399' : '#f87171'}
                          fontSize={9.5}
                          fontWeight={700}
                          fontFamily="'Sora', sans-serif"
                        >
                          ROI: {catRoi >= 0 ? '+' : ''}{catRoi.toFixed(1)}%
                        </text>
                      </>
                    )}

                    <text
                      x={r.x + 12}
                      y={r.y + r.h - 10}
                      fill="#ffffff"
                      fontSize={r.w > 120 ? 12 : 10.5}
                      fontWeight={700}
                      fontFamily="'Sora', sans-serif"
                    >
                      {currency} {fmt(catTotalVal)}
                    </text>
                  </g>
                )}

                {/* Tier 2: Medium Tile */}
                {isMedium && (
                  <g style={{ pointerEvents: 'none' }}>
                    <text
                      x={r.x + 8}
                      y={r.y + 18}
                      fill="#ffffff"
                      fontSize={11}
                      fontWeight={700}
                      fontFamily="'Sora', sans-serif"
                    >
                      {catName.length > 9 ? catName.slice(0, 8) + '.' : catName}
                    </text>
                    <text
                      x={r.x + r.w - 8}
                      y={r.y + 18}
                      textAnchor="end"
                      fill="rgba(255,255,255,0.9)"
                      fontSize={10}
                      fontWeight={800}
                      fontFamily="'Sora', sans-serif"
                    >
                      {pct}%
                    </text>
                    {r.h > 46 && (
                      <text
                        x={r.x + 8}
                        y={r.y + r.h - 9}
                        fill="#ffffff"
                        fontSize={10}
                        fontWeight={700}
                        fontFamily="'Sora', sans-serif"
                      >
                        {currency} {fmt(catTotalVal)}
                      </text>
                    )}
                  </g>
                )}

                {/* Tier 3: Compact Tile (Guarantees all small categories are visible!) */}
                {isSmall && r.w >= 24 && r.h >= 18 && (
                  <g style={{ pointerEvents: 'none' }}>
                    <text
                      x={r.x + r.w / 2}
                      y={r.y + r.h / 2 - 2}
                      textAnchor="middle"
                      fill="#ffffff"
                      fontSize={Math.min(r.w * 0.18, 10)}
                      fontWeight={800}
                      fontFamily="'Sora', sans-serif"
                    >
                      {catName.slice(0, 5)}
                    </text>
                    <text
                      x={r.x + r.w / 2}
                      y={r.y + r.h / 2 + 9}
                      textAnchor="middle"
                      fill="rgba(255,255,255,0.9)"
                      fontSize={Math.min(r.w * 0.16, 9)}
                      fontWeight={700}
                      fontFamily="'Sora', sans-serif"
                    >
                      {pct}%
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Floating Detailed Insight Popover on Hover */}
      {hovered !== null && rects[hovered] && (
        <div
          style={{
            position: 'absolute',
            top: `${Math.min(rects[hovered].y + 10, H - 140)}px`,
            left: `${rects[hovered].x + rects[hovered].w + 10 > W - 150 ? rects[hovered].x - 190 : rects[hovered].x + rects[hovered].w + 10}px`,
            width: '185px',
            backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.96)' : 'rgba(255, 255, 255, 0.98)',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: '16px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.15)',
            padding: '12px',
            zIndex: 50,
            backdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            transition: 'all 0.15s ease-out',
          }}
          className="text-left"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 99, backgroundColor: rects[hovered].item.color }} />
            <h4 style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: "'Sora',sans-serif", margin: 0 }}>
              {rects[hovered].item.name}
            </h4>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: P.subtle, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Value</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: "'Sora',sans-serif" }}>
                {currency} {fmt(rects[hovered].item.totalRaw)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: P.subtle, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{timeframe} Change</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: rects[hovered].item.timeframeValue >= 0 ? '#10b981' : '#ef4444', fontFamily: "'Sora',sans-serif" }}>
                {rects[hovered].item.timeframeValue >= 0 ? '+' : '-'}{currency}{fmt(Math.abs(rects[hovered].item.timeframeValue))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`, paddingTop: 6 }}>
              <div>
                <div style={{ fontSize: 8, fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>ROI</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: rects[hovered].item.roi >= 0 ? '#10b981' : '#ef4444' }}>
                  {rects[hovered].item.roi >= 0 ? '+' : ''}{rects[hovered].item.roi.toFixed(1)}%
                </div>
              </div>
              <div>
                <div style={{ fontSize: 8, fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>Weight</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: P.text }}>
                  {rects[hovered].item.percentOfPortfolio.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Spectrum Strip (Ensures 100% of categories are visible, clickable & identifiable) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 4 }}>
        {data.map((cat, idx) => {
          const isHov = hovered === idx;
          return (
            <div
              key={cat.name}
              onMouseEnter={() => setHovered(idx)}
              onMouseLeave={() => setHovered(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '4px 9px',
                borderRadius: 99,
                background: isHov ? `${cat.color}25` : P.s3,
                border: `1px solid ${isHov ? cat.color : P.border}`,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: 99, background: cat.color }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: P.text }}>{cat.name}</span>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: cat.roi >= 0 ? P.teal : P.rose }}>
                {cat.percentOfPortfolio.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom Reporting Row with Indicators */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 8 }}>
        {/* Indicator 1: Portfolio Value */}
        <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 14, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Portfolio Value
          </span>
          <div style={{ fontSize: 18, fontWeight: 900, color: P.text, fontFamily: "'Sora',sans-serif", marginTop: 3, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currency} {fmt(calculatedPortfolioValue)}
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, color: P.muted, marginTop: 1 }}>Total Assets</span>
        </div>

        {/* Indicator 2: ROI Value */}
        {roiValue !== undefined && (
          <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 14, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Net Alpha Gain
            </span>
            <div style={{ fontSize: 18, fontWeight: 900, color: roiValue >= 0 ? P.teal : P.rose, fontFamily: "'Sora',sans-serif", marginTop: 3, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {roiValue >= 0 ? '+' : '-'}{currency} {fmt(Math.abs(roiValue))}
            </div>
            <span style={{ fontSize: 9, fontWeight: 700, color: P.muted, marginTop: 1 }}>{timeframe} Delta</span>
          </div>
        )}

        {/* Indicator 3: ROI Percentage */}
        <div style={{ background: positive ? 'rgba(45,212,191,.12)' : 'rgba(248,113,113,.12)', border: `1px solid ${positive ? P.teal : P.rose}33`, borderRadius: 14, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: positive ? P.teal : P.rose, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Portfolio ROI
          </span>
          <div style={{ fontSize: 18, fontWeight: 900, color: positive ? P.teal : P.rose, fontFamily: "'Sora',sans-serif", marginTop: 3, letterSpacing: '-0.02em' }}>
            {roi >= 0 ? '+' : ''}{roi.toFixed(1)}%
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, color: positive ? P.teal : P.rose, opacity: 0.8, marginTop: 1 }}>Performance</span>
        </div>

        {/* Indicator 4: Top Exposure */}
        {topCategory && (
          <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 14, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Lead Exposure
            </span>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#3b82f6', fontFamily: "'Sora',sans-serif", marginTop: 3, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {topCategory.name}
            </div>
            <span style={{ fontSize: 9, fontWeight: 700, color: P.muted, marginTop: 1 }}>{topCategory.percentOfPortfolio.toFixed(0)}% Allocation</span>
          </div>
        )}

        {/* Indicator 5: Bucket Count */}
        <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 14, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: P.subtle, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Allocation Spread
          </span>
          <div style={{ fontSize: 18, fontWeight: 900, color: P.text, fontFamily: "'Sora',sans-serif", marginTop: 3, letterSpacing: '-0.02em' }}>
            {data.length} Buckets
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, color: P.muted, marginTop: 1 }}>Active Spread</span>
        </div>
      </div>
    </div>
  );
};

export default PortfolioTreemap;
